import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Formik, Form } from 'formik';
import { Grid, Header, Button, Icon, Form as SemanticForm, Label, Message } from 'semantic-ui-react';
import SimpleAudioPlayer from '../../../components/audioPlayer/ReactUseAudioPlayer';
import { FormikInput, FormikTextArea, FormikField, FormikNumberInput } from '../../../pages/subPages/voiceActor/FormComponents';
import SimpleRecordVariation from '../../../variationWorksheet/components/audio/SimpleRecordVariation';
import { StatusForm, StatusMessage } from '../../../components/statusTools/StatusIndicators';
import useStatusManagement from '../../../hooks/bookCreator/useStatusManagement';
import useStatusMessages from '../../hooks/useStatusMessages';
import VariationVersionsList from '../common/VariationVersionsList_upgrade';
import { useStableEditVersionManager } from '../../hooks/useStableHooks';
import { debounce } from 'lodash';

import { signS3UrlForUser } from '../../../utils/fetchSignedUrlByFolderV2';
import { getErrorMessage } from '../../../utils/fileUtils';

import '../../styles/bookWorksheet.css';

/**
 * UPGRADED Enhanced component for creating and editing variations with audio support
 * Uses centralized Redux state management through useEditVersionManager_upgrade
 * Eliminates complex useEffect patterns in favor of clean action-based updates
 */
const VariationFormUpgrade = ({
  isEditing,
  formikRef,
  selectedVariation,
  selectedCharacter,
  validationSchema,
  signedAudioUrl,
  activeParams,
  setShowDeleteModal,
  selectedBook,
  newVariationId,
  setIsEditing,
  onSampleDelete,
  selectedSampleForNewVariation,
  isCreatingFromSample,
  onCancelCreateFromSample,
  onUseAsBase
}) => {
  // 🚀 PHASE 1: Add render tracking to diagnose re-render issues
  const renderCount = useRef(0);
  renderCount.current++;

  // if (process.env.NODE_ENV === 'development' && window.DEBUG_RENDERS) {
  //   console.log(`[RENDER] VariationForm render #${renderCount.current}`, {
  //     isEditing,
  //     selectedVariationId: selectedVariation?.uuid,
  //     selectedCharacterId: selectedCharacter?.uuid,
  //     isCreatingFromSample,
  //     timestamp: new Date().toISOString()
  //   });
  // }

  // 🚀 STABLE HOOK: Use stable wrapper that only updates when change counter changes
  const {
    // State
    editVariationData,
    activeVersion,
    samples,
    editMode,
    // currentAction,
    loadingState,
    error,
    isDirty,
    hasUnsavedChanges,
    // isSubmitting,
    showErrorHint,
    errorHintMessage,
    // canSave,
    // canCancel,
    // isLoading,
    // formValues,

    // Actions
    loadVariation,
    loadVersion,
    loadDefault,
    loadClone,
    // save,
    // saveWithValidation,
    variationSubmit,
    deleteVersionById,
    deleteVariation,
    setActiveSample,
    updateVariationField,
    updateActiveVersionField,
    // setDirty,
    clearError,
    // showHint,
    clearForm,
    cancel,
    // validateForSave,
    // setActiveVersionFromSamples,
    // Constants
    // ACTION_TYPES,
    EDIT_MODES: EDIT_MODES_CONST,
    LOADING_STATES: LOADING_STATES_CONST
  } = useStableEditVersionManager();

  // ===== LOCAL UI STATE (non-Redux) =====
  const pendingRecordingRef = useRef(null);
  // const [showInlineRecording, setShowInlineRecording] = useState(false);
  // const [audioUpdateCounter, setAudioUpdateCounter] = useState(0);
  // const [playerKey, setPlayerKey] = useState(Date.now());
  const selectionDuration = useRef(0);
  const [isRecordingDisabled, setIsRecordingDisabled] = useState(true);
  // const [isShowPlayer, setIsShowPlayer] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState(null);
  const [currentSignedUrl, setCurrentSignedUrl] = useState('');
  // const [isSigningUrl, setIsSigningUrl] = useState(false);

  // ===== LOCAL FORM STATE (for performance optimization) =====
  const [localFormState, setLocalFormState] = useState({
    content: '',
    hasLocalChanges: false,
    lastSyncedContent: ''
  });
  const [localHasUnsavedChanges, setLocalHasUnsavedChanges] = useState(false);

  // ===== DERIVED VALUES =====
  // Get userId from stable hook data (avoid double hook call)
  const userId = editVariationData?.userId || 'default-user-id';

  // Use null for entity to prevent useStatusManagement from overriding form values
  const statusManagement = useStatusManagement(null, formikRef);

  const {
    priority, setPriority,
    status, setStatus,
    deadline, setDeadline,
    priorityOptions, statusOptions,
    getStatusColor,
    isDeadlineApproaching, isDeadlinePast,
    getDeadlineWarning,
    statusMsgs
  } = statusManagement;

  const { getMessage } = useStatusMessages(statusMsgs);

  // ===== HELPER FUNCTIONS =====
  const getFormValues = useCallback((variation) => ({
    name: variation?.name || '',
    s3Url: variation?.s3Url || '',
    content: activeVersion?.textSnippet || variation?.content || '', // Use active sample content first
    version: variation?.version || 1,
    priority: variation?.priority || 'medium',
    status: variation?.status || 'not-started',
    deadline: variation?.deadline || null,
    signedAudioUrl: signedAudioUrl || ''
  }), [signedAudioUrl, activeVersion]);

  // ===== DEBOUNCED REDUX UPDATES FOR PERFORMANCE =====

  // Create debounced Redux update function for text content
  const debouncedReduxUpdate = useMemo(
    () => debounce((field, value) => {
      // if (process.env.NODE_ENV === 'development') {
      //   console.log('[DEBOUNCED_UPDATE] Updating Redux:', field, value);
      // }
      updateActiveVersionField(field, value);
    }, 100), // 300ms delay
    [updateActiveVersionField]
  );

  // Memoize stable props for VariationVersionsList
  // const stableVariationId = useMemo(() => editVariationData?.uuid, [editVariationData?.uuid]);
  // const stableSamples = useMemo(() => samples, [
  //   samples,
  //   // samples?.length,
  //   samples?.map(s => s.uuid).join(','),
  //   samples?.map(s => s.textSnippet).join(',')
  // ]);
  // const stableActiveSampleUuid = useMemo(() => editVariationData?.activeSampleUuid, [editVariationData?.activeSampleUuid]);
  // const stablePreviewedSampleUuid = useMemo(() => activeVersion?.uuid, [activeVersion?.uuid]);

  // ===== OPTIMIZED FIELD CHANGE HANDLERS =====

  // Handle field changes with performance optimization
  const handleFieldChange = useCallback((field, value) => {
    // Reduce logging in production
    if (process.env.NODE_ENV === 'development') {
      // Conditional logging for performance
      if (process.env.NODE_ENV === 'development' && window.DEBUG_VARIATION_FORM) {
        console.log('[FIELD_CHANGE] User changed field:', field, value);
      }
    }

    if (['name', 'priority', 'status', 'deadline', 'version'].includes(field)) {
      // Non-text fields update immediately (no performance impact)
      updateVariationField(field, value);
      setLocalHasUnsavedChanges(true);
    } else if (['content', 's3Url', 'fileDuration'].includes(field)) {
      // Text content fields use optimized approach
      if (field === 'content') {
        // 1. Update local state immediately for responsive UI
        setLocalFormState(prev => ({
          ...prev,
          content: value,
          hasLocalChanges: true
        }));

        // 2. Set button state immediately
        setLocalHasUnsavedChanges(true);

        // 3. Update Redux with debounce (for data persistence)
        debouncedReduxUpdate('textSnippet', value);
      } else {
        // Non-content version fields update immediately
        const versionField = field === 'content' ? 'textSnippet' : field;
        updateActiveVersionField(versionField, value);
        setLocalHasUnsavedChanges(true);
      }
    }
  }, [updateVariationField, updateActiveVersionField, debouncedReduxUpdate]);

  // Memoize the version action handler to prevent unnecessary re-renders
  const handleVersionAction = useCallback(async (actionPayload) => {
    const { actionType, versionSample, variationId } = actionPayload;
    if (process.env.NODE_ENV === 'development') {
      console.log('[VERSION_ACTION] Processing:', actionType, 'for sample:', versionSample?.uuid);
    }

    switch (actionType) {
      case 'SAMPLE_PREVIEW':
        console.log('[VERSION_ACTION] Preview sample:', versionSample.uuid);
        break;

      case 'SAMPLE_EDIT_SELECT':
        if (versionSample?.uuid && variationId) {
          // Clear local state before version change to allow Redux sync
          console.log('[VERSION_ACTION] Clearing local state for version selection');
          debouncedReduxUpdate.cancel();
          setLocalFormState({
            content: '',
            hasLocalChanges: false,
            lastSyncedContent: ''
          });
          setLocalHasUnsavedChanges(false);
          // ✅ Clear pending recording when switching versions
          pendingRecordingRef.current = null;
          // Load version for editing without changing activeSampleUuid
          console.log('[VERSION_ACTION] Loading version for edit (no activeSampleUuid change):', versionSample.uuid);
          await loadVersion(variationId, versionSample.uuid);
        }
        break;

      case 'SAMPLE_USE_AS_BASE':
        if (variationId && versionSample?.uuid) {
          pendingRecordingRef.current = null;
          await loadClone(variationId, versionSample.uuid);
        }
        break;

      case 'SET_ACTIVE_SAMPLE':
        if (variationId && versionSample?.uuid) {
          await setActiveSample(variationId, versionSample.uuid);
        }
        break;

      case 'DELETE_VERSION':
        if (versionSample?.uuid && variationId) {
          await deleteVersionById(versionSample.uuid, variationId);
          // Refresh the current variation after deletion
          if (editVariationData?.uuid) {
            await loadVariation(editVariationData.uuid);
          }
        }
        break;

      case 'VARIATION_DELETED':
        console.log('[VERSION_ACTION] Variation deleted successfully:', variationId);
        clearForm();
        if (onSampleDelete) {
          onSampleDelete();
        }
        break;

      default:
        console.warn('[VERSION_ACTION] Unknown action type:', actionType);
    }
  }, [loadVersion, loadClone, setActiveSample, deleteVersionById, loadVariation, editVariationData, clearForm, onSampleDelete, debouncedReduxUpdate]);

  // ===== LIFECYCLE MANAGEMENT - SIMPLIFIED =====

  // Load variation when selectedVariation changes
  useEffect(() => {
    if (selectedVariation?.uuid && isEditing && !isCreatingFromSample) {
      // console.log('[LIFECYCLE] Loading variation for editing:', selectedVariation.uuid);
      pendingRecordingRef.current = null; // Clear when switching variations
      loadVariation(selectedVariation.uuid);
    }
  }, [selectedVariation?.uuid, isEditing, isCreatingFromSample, loadVariation]);

  // Load default for new variation
  useEffect(() => {
    if (!selectedVariation && !isCreatingFromSample && selectedCharacter?.uuid) {
      // console.log('[LIFECYCLE] Loading default for new variation');
      loadDefault();
    }
  }, [selectedVariation, isCreatingFromSample, selectedCharacter?.uuid, loadDefault]);

  // Handle create from sample mode
  useEffect(() => {
    if (isCreatingFromSample && selectedSampleForNewVariation && selectedVariation?.uuid) {
      // console.log('[LIFECYCLE] Loading clone for create from sample');
      loadClone(selectedVariation.uuid, selectedSampleForNewVariation.uuid);
    }
  }, [isCreatingFromSample, selectedSampleForNewVariation, selectedVariation?.uuid, loadClone]);

  // ===== OPTIMIZED FORM SYNCHRONIZATION =====

  // Track the last synced variation and version to prevent unnecessary syncs
  const lastSyncedRef = useRef({
    variationId: null,
    versionId: null,
    content: null
  });

  // Single consolidated form sync effect - prevents multiple sync calls and respects user input
  useEffect(() => {
    if (!formikRef.current) return;

    // Determine what type of sync we need
    const currentVariationId = editVariationData?.uuid;
    const currentVersionId = activeVersion?.uuid;
    const currentContent = activeVersion?.textSnippet;

    // Check if this is actually a new variation or version
    const isNewVariation = currentVariationId && currentVariationId !== lastSyncedRef.current.variationId;
    const isNewVersion = currentVersionId && currentVersionId !== lastSyncedRef.current.versionId;
    const isContentChanged = currentContent !== lastSyncedRef.current.content;

    // Only sync if we have actual changes and no local user input
    if (isNewVariation && currentVariationId) {
      // Full form sync for new variation
      const formData = getFormValues(editVariationData);

      if (process.env.NODE_ENV === 'development') {
        console.log('[FORM_SYNC] Full sync - NEW variation loaded:', {
          variationId: currentVariationId,
          content: formData.content?.substring(0, 50) + '...'
        });
      }

      // Batch all form updates together
      formikRef.current.setValues(formData, false);
      formikRef.current.setTouched({}, false);

      // Update local state to match
      setLocalFormState({
        content: formData.content || '',
        lastSyncedContent: formData.content || '',
        hasLocalChanges: false
      });
      setLocalHasUnsavedChanges(false);

      // Update tracking
      lastSyncedRef.current = {
        variationId: currentVariationId,
        versionId: currentVersionId,
        content: currentContent
      };

    } else if (isNewVersion && currentVersionId && !localFormState.hasLocalChanges) {
      // Content-only sync for version changes (only if no local changes)
      if (process.env.NODE_ENV === 'development') {
        console.log('[FORM_SYNC] Content sync - NEW version selected:', {
          versionId: currentVersionId,
          content: currentContent?.substring(0, 50) + '...',
          hasLocalChanges: localFormState.hasLocalChanges
        });
      }

      // Only update content field
      formikRef.current.setFieldValue('content', currentContent || '', false);

      // Update local state to match
      setLocalFormState(prev => ({
        ...prev,
        content: currentContent || '',
        lastSyncedContent: currentContent || '',
        hasLocalChanges: false
      }));

      // Update tracking
      lastSyncedRef.current = {
        variationId: currentVariationId,
        versionId: currentVersionId,
        content: currentContent
      };

    } else if (process.env.NODE_ENV === 'development' && (isContentChanged || localFormState.hasLocalChanges)) {
      // Debug logging for skipped syncs
      console.log('[FORM_SYNC] Skipping sync - user has local changes or content unchanged:', {
        isNewVariation,
        isNewVersion,
        isContentChanged,
        hasLocalChanges: localFormState.hasLocalChanges,
        currentContent: currentContent?.substring(0, 50) + '...',
        lastContent: lastSyncedRef.current.content?.substring(0, 50) + '...'
      });
    }
  }, [
    editVariationData,
    editVariationData?.uuid,
    activeVersion?.uuid,
    activeVersion?.textSnippet,
    localFormState.hasLocalChanges,
    formikRef,
    getFormValues
  ]);

  // ===== AUDIO MANAGEMENT =====

  // Sign URL when activeVersion s3Url changes
  useEffect(() => {
    const handleS3UrlChange = async () => {
      if (activeVersion?.s3Url && activeVersion.s3Url !== 'pending-recording') {
        const newSignedUrl = await signS3UrlForUser(userId, activeVersion.s3Url);
        setCurrentSignedUrl(newSignedUrl);
        selectionDuration.current = Number(activeVersion.fileDuration) || 0;
      } else if (!activeVersion?.s3Url) {
        setCurrentSignedUrl('');
        selectionDuration.current = 0;
      }
    };

    handleS3UrlChange();
  }, [userId, activeVersion?.s3Url, activeVersion?.fileDuration]);


  // ===== EXISTING FUNCTIONS (mostly unchanged) =====

  const getNextVersionNumber = useCallback(() => {
    const currentVersion = editVariationData?.version || 1;
    return currentVersion + 1;
  }, [editVariationData?.version]);

  const resetPlayer = useCallback(() => {
    // setPlayerKey(null);
    // setTimeout(() => {
    //   setPlayerKey(Date.now());
    // }, 50);
  }, []);

  const handleReload = useCallback(() => {
    resetPlayer();
  }, [resetPlayer]);

  const handleRecordingComplete = useCallback((recordingData) => {
    setRecordingStatus(null);
    if (formikRef.current) {
      formikRef.current.setFieldValue('s3Url', 'pending-recording');
      formikRef.current.setFieldTouched('s3Url', true);
    }
    pendingRecordingRef.current = recordingData;
    setLocalHasUnsavedChanges(true); // ✅ This is sufficient

  }, [formikRef]);

  const handleRecordingDiscard = useCallback(() => {
    pendingRecordingRef.current = null;
    if (formikRef.current) {
      formikRef.current.setFieldValue('s3Url', '');
    }
  }, [formikRef]);

  // const handleUseAsBase = useCallback((versionSample) => {
  //   console.log('Using sample as base for new variation:', versionSample);
  //   if (onUseAsBase) {
  //     onUseAsBase(versionSample);
  //   }
  // }, [onUseAsBase]);

  const updateRecordingDisabled = useCallback(() => {
    if (!formikRef.current) {
      console.log('[RECORDING] No formikRef, keeping disabled');
      return;
    }

    const values = formikRef.current.values;
    const nameEmpty = !values.name || values.name.trim() === '';
    const contentEmpty = !values.content || values.content.trim() === '';
    const shouldDisable = nameEmpty || contentEmpty;

    setIsRecordingDisabled(shouldDisable);
  }, [formikRef]);

  // ===== EFFECTS =====
  useEffect(() => {
    updateRecordingDisabled();
  }, [updateRecordingDisabled]);

  useEffect(() => {
    if (formikRef.current) {
      updateRecordingDisabled();
    }
  }, [formikRef, formikRef.current?.values?.name, formikRef.current?.values?.content, updateRecordingDisabled]);

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.resetPlayer = resetPlayer;
    }
  }, [formikRef, resetPlayer]);

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.setFieldValue('signedAudioUrl', signedAudioUrl || '');
      if (signedAudioUrl) {
        // setAudioUpdateCounter(prev => prev + 1);
        resetPlayer();
      }
      // setIsShowPlayer(signedAudioUrl);
    }
  }, [formikRef, signedAudioUrl, resetPlayer]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedReduxUpdate.cancel();
    };
  }, [debouncedReduxUpdate]);

  // ===== RENDER HELPERS =====
  const AudioPlayerContainer = useCallback(() => {
    const showPlayer = currentSignedUrl && !recordingStatus;
    const showMessage = !currentSignedUrl || recordingStatus;

    let messageContent = '';
    if (!currentSignedUrl) {
      messageContent = 'No audio available';
    } else if (recordingStatus === 'recording') {
      const nextVersion = getNextVersionNumber();
      messageContent = `Re-recording "${editVariationData?.name || 'New Variation'}". Making version ${nextVersion}`;
    }

    return (
      <div className="audio-player-fixed-container">
        <div style={{
          display: showPlayer ? 'block' : 'none',
          height: '100%',
          width: '100%'
        }}>
          {currentSignedUrl && (
            <SimpleAudioPlayer
              key={`player-${editVariationData?.uuid || 'new'}-${editVariationData?.version || 1}`}
              header={`Variation: ${editVariationData?.name || 'Audio'}`}
              src={currentSignedUrl}
              fileDuration={Number(selectionDuration.current) || 5}
              autoPlay={false}
              onReload={handleReload}
            />
          )}
        </div>
        {showMessage && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(249, 249, 249, 0.95)',
            color: '#666',
            fontSize: '14px',
            textAlign: 'center',
            padding: '20px'
          }}>
            <div>
              <Icon name={recordingStatus === 'recording' ? 'microphone' : 'info circle'} />
              <div style={{ marginTop: '8px' }}>
                {messageContent}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }, [currentSignedUrl, recordingStatus, editVariationData?.name, getNextVersionNumber, editVariationData?.version, editVariationData?.uuid, handleReload]);

  const SuccessMessageContainer = useCallback(({ messages = [] }) => {
    if (messages.length === 0) return null;
    return (
      <div className='success-message-container'>
        <Icon name="check circle" color="green" />
        <span className="success-message-text">
          {messages.map((message, index) => (
            <div key={index}>{message}</div>
          ))}
        </span>
      </div>
    );
  }, []);

  const InfoMessageContainer = useCallback(({ message }) => {
    if (!message || message.length === 0) return null;
    return (
      <div className='infoMsgContatiner'>
        <div className='infoMsg'>
          {message}
        </div>
      </div>
    );
  }, []);

  const getFormMode = () => {
    if (editMode === EDIT_MODES_CONST.CLONE) {
      return {
        header: 'Create New Variation',
        mode: 'create-from-sample'
      };
    } else if (editMode === EDIT_MODES_CONST.EDIT) {
      return {
        header: 'Edit Variation',
        mode: 'edit'
      };
    } else {
      return {
        header: 'New Variation',
        mode: 'create-new'
      };
    }
  };

  const formMode = getFormMode();

  // Show error state
  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <Message negative>
          <Icon name="exclamation triangle" />
          Error: {getErrorMessage(error)}
          <Button
            size="mini"
            floated="right"
            onClick={clearError}
          >
            Dismiss
          </Button>
        </Message>
      </div>
    );
  }

  // Don't render if we don't have edit version data yet
  if (!editVariationData && !activeVersion) {
    // console.log('[FORM_RENDER] Showing initializing - editVariationData:', !!editVariationData, 'activeVersion:', !!activeVersion, 'loadingState:', loadingState);
    return <div>Initializing...</div>;
  }

  return (
    <div className="bw-variation-form-container">
      <Header as="h3" dividing>
        {formMode.header}
        {editMode === EDIT_MODES_CONST.CLONE && (
          <Label color='blue' size='small' style={{ marginLeft: '10px' }}>
            <Icon name='copy' />
            Based on "{editVariationData?.name}" v{activeVersion?.version}
          </Label>
        )}
      </Header>

      {editMode === EDIT_MODES_CONST.CLONE && (
        <Message info style={{ marginBottom: '15px' }}>
          <Icon name='copy' />
          Creating new variation based on version {activeVersion?.version}
          of "{editVariationData?.name}". The text and audio from that version will be used as the starting point.
          <Button
            floated='right'
            size='mini'
            onClick={onCancelCreateFromSample}
            style={{ marginTop: '-5px' }}
          >
            <Icon name='cancel' />
            Cancel
          </Button>
        </Message>
      )}

      {showErrorHint && errorHintMessage && (
        <Message warning style={{ marginBottom: '15px' }}>
          <Icon name='warning' />
          {errorHintMessage}
        </Message>
      )}

      {isEditing && signedAudioUrl && signedAudioUrl === '' && (
        <SuccessMessageContainer
          messages={['Variation created successfully! You can now record voice for this variation.']}
        />
      )}

      <Formik
        innerRef={formikRef}
        enableReinitialize={false}
        initialValues={getFormValues(editVariationData)}
        validationSchema={validationSchema}

        onSubmit={async (values) => {
          console.log('[FORM_SUBMIT] Using new centralized variationSubmit with editMode:', editMode);
          console.log(`[FORM_SUBMIT] HAS CHANGES: ${localFormState.hasLocalChanges} localFormState:`, localFormState);
          console.log(`[FORM_SUBMIT] PENDING RECORDING:`, !!pendingRecordingRef.current?.audioBlob);

          // ✅ CLEAR PENDING RECORDING IMMEDIATELY when submission starts
          const currentPendingRecording = pendingRecordingRef.current;
          pendingRecordingRef.current = null;

          // Cancel any pending debounced updates
          debouncedReduxUpdate.cancel();

          // Call the centralized submit function with the captured recording
          const result = await variationSubmit(localFormState, currentPendingRecording);

          console.log('SUBMIT COMPLETED', result);
          if (result.success) {
            // Reset local state after successful save
            setLocalFormState(prev => ({ ...prev, hasLocalChanges: false }));
            setLocalHasUnsavedChanges(false);
          } else {
            // ✅ RESTORE PENDING RECORDING if submission failed
            pendingRecordingRef.current = currentPendingRecording;
          }

          return result.success;
        }}


        onReset={(values) => {
          console.log('DEBUG: Formik onReset called with values:', values);
        }}
      >
        {({ isSubmitting, values, setFieldValue, dirty, errors, touched }) => {
          return (
            <Form as={SemanticForm}>
              <Grid divided={false}>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <FormikField
                      name="name"
                      component={FormikInput}
                      label="Variation Name*"
                      fluid
                      placeholder="Enter variation name"
                      onChange={(e) => {
                        formikRef.current.handleChange(e);
                        handleFieldChange('name', e.target.value);
                        setTimeout(() => updateRecordingDisabled(), 0);
                      }}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className="noTopPad">
                  <Grid.Column width={16}>
                    <FormikField
                      name="content"
                      component={FormikTextArea}
                      label="Content Text*"
                      placeholder="Enter text content for recording"
                      rows={3}
                      onChange={(e) => {
                        formikRef.current.handleChange(e);
                        handleFieldChange('content', e.target.value);
                        setTimeout(() => updateRecordingDisabled(), 0);
                      }}

                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className="noTopPad">
                  <Grid.Column width={16}>
                    <InfoMessageContainer
                      message={!currentSignedUrl ? getMessage('PLAYER_INFO', 'VARIATION_NOT_RECORDED') :
                        getMessage('PLAYER_INFO', 'VARIATION_CREATED', { duration: Number(selectionDuration.current) || 5 })
                      }
                    />

                    <AudioPlayerContainer />

                    <fieldset
                      className='audeo-section-fieldset'
                      disabled={isRecordingDisabled}
                      style={{ opacity: isRecordingDisabled ? 0.6 : 1 }}
                    >
                      <div className="recording-container">
                        <div className="recording-control-container">
                          <Header as="h4" style={{ margin: 0, marginLeft: '5px' }}>
                            Voice Recording
                            {pendingRecordingRef.current && (
                              <Label color="green" size="tiny" style={{ marginLeft: '10px' }}>
                                <Icon name="check" />
                                Recording Ready
                              </Label>
                            )}
                          </Header>
                        </div>
                        <SimpleRecordVariation
                          isActive={true}
                          onClose={() => {
                            // setShowInlineRecording(false);
                            setRecordingStatus(null);
                            if (!pendingRecordingRef.current) {
                              handleRecordingDiscard();
                            }
                          }}
                          onRecordingStatusChange={(status) => {
                            if (status === 'recording') {
                              setRecordingStatus('recording');
                            } else if (status === 'stopped' || status === 'completed') {
                              setRecordingStatus(null);
                            }
                          }}
                          selectedBook={selectedBook}
                          selectedCharacter={selectedCharacter}
                          selectedVariation={selectedVariation}
                          contentText={values.content}
                          onSaveRecording={handleRecordingComplete}
                          newVariationId={editVariationData?.uuid}
                          key={`recording-controls-edit-${editVariationData?.uuid || 'new'}`}
                        />
                      </div>
                    </fieldset>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className="noTopPad">
                  <Grid.Column width={16}>
                    <div style={{ marginTop: '20px' }}>
                      <Header as="h4">Status Information</Header>
                      <div style={{ position: 'relative', zIndex: 10 }}>
                        <StatusForm
                          charName={values.name}
                          priority={priority}
                          setPriority={(value) => {
                            setPriority(value);
                            handleFieldChange('priority', value);
                          }}
                          status={status}
                          setStatus={(value) => {
                            setStatus(value);
                            handleFieldChange('status', value);
                          }}
                          deadline={deadline}
                          setDeadline={(value) => {
                            setDeadline(value);
                            handleFieldChange('deadline', value);
                          }}
                          priorityOptions={priorityOptions}
                          statusOptions={statusOptions}
                        />
                        <div style={{ display: 'block', width: '140px !important', position: 'absolute', bottom: '20px', left: '170px' }}>
                          <StatusMessage
                            status={status}
                            deadline={deadline}
                            isDeadlineApproaching={isDeadlineApproaching}
                            isDeadlinePast={isDeadlinePast}
                            getDeadlineWarning={getDeadlineWarning}
                            getStatusColor={getStatusColor}
                          />
                        </div>
                        <div style={{ display: 'block', maxWidth: '70px !important', position: 'absolute', top: '80px', right: '0px' }}>
                          <FormikField
                            name="version"
                            component={FormikNumberInput}
                            maxInputWidth='70px'
                            label="Version*"
                            min={1}
                            step={1}
                            fluid
                            onChange={(e, { value }) => {
                              const numValue = parseInt(value) || 1;
                              formikRef.current.setFieldValue('version', numValue);
                              handleFieldChange('version', numValue);
                            }}
                          />
                        </div>
                      </div>

                    </div>
                  </Grid.Column>
                </Grid.Row>

                {(() => {
                  const shouldShow = isEditing && editVariationData && editMode !== EDIT_MODES_CONST.CLONE;
                  console.log('[FORM_RENDER] VariationVersionsList condition check:', {
                    isEditing,
                    editVariationData: !!editVariationData,
                    editMode,
                    shouldShow,
                    samples: samples?.length,
                    activeSampleUuid: editVariationData?.activeSampleUuid
                  });

                  // 🔍 DETAILED PROPS LOGGING - Track what's being passed to VariationVersionsList
                  if (shouldShow) {
                    console.log('[VERSIONS_LIST_PROPS] Props being passed to VariationVersionsList:', {
                      variationId: editVariationData.uuid,
                      activeSampleUuid: editVariationData?.activeSampleUuid,
                      previewedSampleUuid: activeVersion?.uuid,
                      samples: samples?.map(s => ({
                        uuid: s.uuid,
                        isActive: s.isActive,
                        version: s.version,
                        textSnippet: s.textSnippet?.substring(0, 50) + '...'
                      })),
                      editVariationData: editVariationData ? {
                        uuid: editVariationData.uuid,
                        activeSampleUuid: editVariationData.activeSampleUuid,
                        name: editVariationData.name
                      } : null,
                      activeVersion: activeVersion ? {
                        uuid: activeVersion.uuid,
                        version: activeVersion.version
                      } : null
                    });
                  }

                  return shouldShow ? (
                    <Grid.Row className="noTopPad">
                      <Grid.Column width={16}>
                        <VariationVersionsList
                          variationId={editVariationData.uuid}
                          characterId={selectedCharacter?.uuid}
                          samples={samples}
                          activeSampleUuid={editVariationData.activeSampleUuid}
                          previewedSampleUuid={activeVersion?.uuid}            // ✅ ADD THIS - Blue background
                          onAction={handleVersionAction}
                          isLoading={loadingState === LOADING_STATES_CONST.LOADING}
                        />
                      </Grid.Column>
                    </Grid.Row>
                  ) : null;
                })()}

                <Grid.Row className="noTopPad">
                  <Grid.Column width={16}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%'
                    }}>
                      <div>
                        <Button
                          primary
                          type="submit"
                          disabled={isSubmitting || !values.name || !values.content || (isEditing && !localHasUnsavedChanges && !hasUnsavedChanges && editMode !== EDIT_MODES_CONST.CLONE)}
                          loading={isSubmitting}
                        >
                          {editMode === EDIT_MODES_CONST.CLONE ? 'Create Variation from Sample' :
                            editMode === EDIT_MODES_CONST.EDIT ? 'Update Variation' :
                              (pendingRecordingRef.current ? 'Create Variation with Recording' : 'Create Variation')}
                        </Button>

                        {isEditing && editMode !== EDIT_MODES_CONST.CLONE && (
                          <Button
                            onClick={() => {
                              // Enhanced cancel logic that handles local state
                              debouncedReduxUpdate.cancel();
                              setLocalFormState({
                                content: '',
                                hasLocalChanges: false,
                                lastSyncedContent: ''
                              });
                              setLocalHasUnsavedChanges(false);
                              pendingRecordingRef.current = null;
                              cancel();
                              // Reset form to Redux state
                              if (formikRef.current && activeVersion) {
                                formikRef.current.setFieldValue('content', activeVersion.textSnippet || '', false);
                              }
                            }}
                            disabled={!localHasUnsavedChanges && !hasUnsavedChanges && !isDirty}
                            style={{ marginLeft: '10px' }}
                          >
                            Cancel Changes
                          </Button>
                        )}

                        {(editMode === EDIT_MODES_CONST.NEW || editMode === EDIT_MODES_CONST.CLONE) && (
                          <Button
                            onClick={() => {
                              if (editMode === EDIT_MODES_CONST.CLONE && onCancelCreateFromSample) {
                                onCancelCreateFromSample();
                              } else {
                                clearForm();
                                if (formikRef.current) {
                                  formikRef.current.resetForm();
                                }
                              }
                            }}
                            style={{ marginLeft: '10px' }}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                      <div>
                        {isEditing && editMode !== EDIT_MODES_CONST.CLONE && (
                          <Button negative onClick={async () => {
                            if (editVariationData?.uuid) {
                              await deleteVariation(editVariationData.uuid);
                            }
                          }}>
                            Delete Variation
                          </Button>
                        )}
                      </div>
                    </div>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </div >
  );
};

// Phase 3: Memoize component with custom comparison function
const arePropsEqual = (prevProps, nextProps) => {
  // Only re-render if these critical props change
  return (
    prevProps.isEditing === nextProps.isEditing &&
    prevProps.selectedVariation?.uuid === nextProps.selectedVariation?.uuid &&
    prevProps.selectedCharacter?.uuid === nextProps.selectedCharacter?.uuid &&
    prevProps.signedAudioUrl === nextProps.signedAudioUrl &&
    prevProps.isCreatingFromSample === nextProps.isCreatingFromSample &&
    prevProps.selectedSampleForNewVariation?.uuid === nextProps.selectedSampleForNewVariation?.uuid &&
    prevProps.newVariationId === nextProps.newVariationId &&
    // Function props should be stable with useCallback
    prevProps.setShowDeleteModal === nextProps.setShowDeleteModal &&
    prevProps.setIsEditing === nextProps.setIsEditing &&
    prevProps.onSampleDelete === nextProps.onSampleDelete &&
    prevProps.onUseAsBase === nextProps.onUseAsBase &&
    prevProps.onCancelCreateFromSample === nextProps.onCancelCreateFromSample
  );
};

export default React.memo(VariationFormUpgrade, arePropsEqual);
